/* =========================================================================
   ClueVine — Stats context
   A thin persistence + React wrapper over the pure stats logic. Records
   completed puzzles, exposes per-mode stats, the daily ledger, and the
   recent-answers set used for avoid-recent generation, and supports clearing
   history / full replace (import). Stored or imported data is normalized so a
   malformed payload can never crash rendering, and another tab's writes are
   picked up so two open windows don't overwrite each other's results.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { uuid } from '../utils/id';
import { loadSlice, onSliceChange, saveSlice, type SliceConfig } from './persistence';
import {
  applyResult,
  dailyRecord,
  emptyStats,
  getModeStats,
  migrateStatsV1,
  normalizeStats,
  type DailyRecord,
  type ModeStats,
  type ResultRecord,
  type StatsState,
} from './statsLogic';

const slice: SliceConfig<StatsState> = {
  name: 'stats',
  version: 2,
  defaults: () => emptyStats(),
  migrate: (from, data) => (from === 1 ? migrateStatsV1(data) : null),
};

const load = (): StatsState => normalizeStats(loadSlice(slice));

interface StatsContextValue {
  stats: StatsState;
  record: (record: Omit<ResultRecord, 'historyId'>) => void;
  modeStats: (modeId: string) => ModeStats;
  daily: (day: number, modeId: string) => DailyRecord | undefined;
  recentAnswers: (modeId: string) => Set<string>;
  clearHistory: () => void;
  replaceAll: (next: unknown) => void;
  reset: () => void;
}

const StatsContext = createContext<StatsContextValue | null>(null);

export function StatsProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<StatsState>(load);

  useEffect(() => {
    saveSlice(slice, stats);
  }, [stats]);

  // Another tab/window recorded a result: adopt its copy so ours doesn't clobber it.
  useEffect(() => onSliceChange('stats', () => setStats(load())), []);

  const record = useCallback((record: Omit<ResultRecord, 'historyId'>) => {
    // Apply on top of what's in storage right now (another tab may have written since).
    setStats(() => applyResult(load(), { ...record, historyId: uuid() }));
  }, []);

  const modeStats = useCallback((modeId: string) => getModeStats(stats, modeId), [stats]);
  const daily = useCallback((day: number, modeId: string) => dailyRecord(stats, day, modeId), [stats]);

  const recentAnswers = useCallback(
    (modeId: string) => new Set(getModeStats(stats, modeId).recentAnswers),
    [stats],
  );

  const clearHistory = useCallback(() => setStats((s) => ({ ...s, history: [] })), []);
  const replaceAll = useCallback((next: unknown) => setStats(normalizeStats(next)), []);
  const reset = useCallback(() => setStats(emptyStats()), []);

  const value = useMemo<StatsContextValue>(
    () => ({ stats, record, modeStats, daily, recentAnswers, clearHistory, replaceAll, reset }),
    [stats, record, modeStats, daily, recentAnswers, clearHistory, replaceAll, reset],
  );

  return <StatsContext.Provider value={value}>{children}</StatsContext.Provider>;
}

export function useStats(): StatsContextValue {
  const ctx = useContext(StatsContext);
  if (!ctx) throw new Error('useStats must be used within <StatsProvider>');
  return ctx;
}
